import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectWithoutProperties from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
var _excluded = ["post"];
import React from "react";
var __jsx = React.createElement;
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import { useSession } from "@/_MiniApps/auth/AuthContext";
import { api__getFinanceOrderDetails, FINANCE_POST_INCLUDE } from "@/apis/orders/orders";
import ReservedPostException from "@/exceptions/postExceptions/ReservedPostException";
import api__createArib from "@/apis/arib/api__createArib";
import api__aribSendOtp from "@/apis/arib/api__aribSendOtp";
import api__verifyAribOtp from "@/apis/arib/api__verifyAribOtp";
import useLocalStorage from "@hooks/useLocalStorage";
import api__deleteFinanceOrder from "@/apis/freeFinance/api__deleteFinanceOrder";
import { useLang } from "@/common/Contexts/LanguageContext";
import arib_bank_list_ar from "@data/lookups/arib_bank_list_ar.json";
import arib_bank_list_en from "@data/lookups/arib_bank_list_en.json";
import arib_work_list_ar from "@data/lookups/arib_work_list_ar.json";
import arib_work_list_en from "@data/lookups/arib_work_list_en.json";
import useHelpers from "@hooks/useHelpers";
import api__createAribOrder from "@/apis/arib/api__createAribOrder";
import { trackEvents } from "@/common/events/events";
import { CALCULATOR_DATA_UPDATED_EVENT, readInstallmentCalculatorData, writeInstallmentCalculatorData } from "@/utils/installmentCalculatorStorage";
var extractAndValidationQueryParams = function extractAndValidationQueryParams() {
  var searchParams = new URLSearchParams(__IS_CSR__ ? window.location.search : "");
  var financestep = Number(searchParams.get("financestep")) || 1;
  var post_id = Number(searchParams.get("post_id"));
  var order_id = Number(searchParams.get("order_id"));
  var ref = searchParams.get("ref") === 'aribForm' ? 'aribForm' : null;
  return {
    post_id: post_id,
    financestep: financestep,
    ref: ref
  };
};
var AribFinanceContext = /*#__PURE__*/createContext(null);
export function useAribFinance() {
  return useContext(AribFinanceContext);
}

// @ts-ignore
export var AribFinanceContextProvider = function AribFinanceContextProvider(_ref) {
  var children = _ref.children;
  var _useTranslation = useTranslation(["web_checkout_finance"]),
    t = _useTranslation.t;
  var lang = useLang();
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;
  var _useSession = useSession(),
    sessionUser = _useSession.user;
  var _useState = useState(null),
    fetching_error = _useState[0],
    setFetching_error = _useState[1];
  var _useState2 = useState(true),
    fetch_loading = _useState2[0],
    setFetch_loading = _useState2[1];
  var _useLocalStorage = useLocalStorage(false),
    getLocalStorage = _useLocalStorage.getLocalStorage,
    setLocalStorage = _useLocalStorage.setLocalStorage,
    removeLocalStorage = _useLocalStorage.removeLocalStorage;
  var _useState3 = useState(null),
    postData = _useState3[0],
    setPostData = _useState3[1];
  var _useHelpers2 = useHelpers(),
    createUrlAbsolute = _useHelpers2.createUrlAbsolute;
  var queryParams = extractAndValidationQueryParams();
  // Key owner for the calculator snapshot. Falls back to postData.post_id on
  // purpose: this provider is mounted at the app root and reads
  // window.location.search directly instead of useLocation(), so it does NOT
  // re-render on client-side navigation — queryParams can still say post_id=0
  // long after the URL gained one. postData is set by the page's own fetch,
  // which does re-render this provider and re-evaluates queryParams, so
  // together the two cover every entry order.
  var calculatorStorageId = queryParams.post_id || (postData === null || postData === void 0 ? void 0 : postData.post_id);
  // The post id is in the URL, so the snapshot is read on the FIRST render rather
  // than in an effect waiting for postData. Child effects run before the parent's,
  // so a late read let FinanceDetailsBox persist API-derived values over the
  // calculator snapshot before this provider had a chance to load it.
  var _useState4 = useState(function () {
      return readInstallmentCalculatorData(calculatorStorageId);
    }),
    installmentCalculatorData = _useState4[0],
    setInstallmentCalculatorData = _useState4[1];
  var _useState5 = useState(queryParams.financestep),
    highestStepReached = _useState5[0],
    setHighestStepReached = _useState5[1];
  var accountSwitchInProgressRef = useRef(false);
  var _useState6 = useState(function () {
      var defaultUserInfo = {
        whatsapp_optin: 1,
        first_name: null,
        fathers_name: null,
        family_name: null,
        birth_date: null,
        birth_date_calendar_type: "G",
        mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null,
        city_id: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.city_id) || null,
        gender: null,
        id_number: null
      };
      var stored = getLocalStorage("arib_userInfo");
      if (!stored) return defaultUserInfo;
      var parsedStored = JSON.parse(stored);
      // warranty_id is per-car, not per-user. Drop any legacy value persisted in
      // arib_userInfo so it can't leak onto a different car's order.
      delete parsedStored.warranty_id;

      // Create merged object with non-null values from storage
      return _objectSpread(_objectSpread({}, defaultUserInfo), Object.fromEntries(Object.entries(parsedStored).filter(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
          _ = _ref3[0],
          value = _ref3[1];
        return value !== null && value !== "";
      })));
    }),
    userInfo = _useState6[0],
    setUserInfo = _useState6[1];

  // Selected warranty is per-car. Persisted under a post-scoped key (NOT in
  // arib_userInfo) so it survives browser-back without leaking onto another car.
  var warrantyStorageKey = "arib_warranty_".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id);
  var _useState7 = useState(function () {
      var stored = getLocalStorage(warrantyStorageKey);
      return stored ? Number(stored) : null;
    }),
    warrantyId = _useState7[0],
    setWarrantyId = _useState7[1];
  // Roadside Assistance selection (SE-12695): 0 = off, <id> = on.
  // SE-12880: persisted per-post so the toggle survives browser-back from payment.
  var roadServiceStorageKey = "arib_road_service_".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id);
  var _useState8 = useState(function () {
      var stored = getLocalStorage(roadServiceStorageKey);
      return stored != null && stored !== "" ? Number(stored) : null;
    }),
    roadServiceId = _useState8[0],
    setRoadServiceId = _useState8[1];

  // Drop warranty keys from other car posts — no need to keep them forever
  // and bloat localStorage. Only the current post's key survives.
  useEffect(function () {
    if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
    Object.keys(window.localStorage).forEach(function (key) {
      if (key.startsWith("arib_warranty_") && key !== warrantyStorageKey) {
        removeLocalStorage(key);
      }
      if (key.startsWith("arib_road_service_") && key !== roadServiceStorageKey) {
        removeLocalStorage(key);
      }
    });
  }, [warrantyStorageKey, roadServiceStorageKey]);
  var _useState9 = useState(function () {
      var stored = getLocalStorage("arib_workInfo");
      return stored ? JSON.parse(stored) : {
        work_sector: null,
        salary: null,
        salary_transfer_method: null,
        employer_name: null,
        termsAccepted: false,
        other_financial_liabilities: null,
        traffic_fine: null,
        total_financial_liabilities: 0,
        traffic_fine_value: 0
      };
    }),
    workInfo = _useState9[0],
    setWorkInfo = _useState9[1];
  var _useState0 = useState(function () {
      var stored = getLocalStorage("arib_orderData");
      return stored ? JSON.parse(stored) : {
        order_id: null,
        order_uuid: null,
        transaction_id: null,
        skipToUploadPage: null,
        reachAribOtpStep: false
      };
    }),
    orderData = _useState0[0],
    setOrderData = _useState0[1];
  var paymentProcessingUrl = useMemo(function () {
    return createUrlAbsolute("/process-payment-moyasar-callback", {
      order_id: orderData === null || orderData === void 0 ? void 0 : orderData.order_id,
      post_id: postData === null || postData === void 0 ? void 0 : postData.post_id
    }).replace('localhost:3000', 'https://local.syarah.com');
  }, [orderData === null || orderData === void 0 ? void 0 : orderData.order_id, postData === null || postData === void 0 ? void 0 : postData.post_id, createUrlAbsolute]);
  var _useState1 = useState(function () {
      var stored = getLocalStorage("arib_DataAfterSubmit");
      return stored ? JSON.parse(stored) : {
        postId: null,
        isOrderSubmited: null
      };
    }),
    dataAfterSubmit = _useState1[0],
    setDataAfterSubmit = _useState1[1];

  /*********************/

  useEffect(function () {
    if (queryParams.financestep === 1 && highestStepReached == 7) {
      setUserInfo(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          id_number: null
        });
      });
      setHighestStepReached(queryParams.financestep);
    }
    //disable this area to make back work normal else if user try to return after any steps it navigates to post view
    // if(window.location.href.indexOf("financestep") === -1){
    //     return;
    // }
    // if (queryParams.financestep < highestStepReached && queryParams.financestep !== 5) {
    //     action__clearLocalStorageData()
    //     navigateTo("/cardetail/" + postData?.post_id + "?ref=error-multiple-actions");
    // } else
    if (queryParams.financestep > highestStepReached) {
      setHighestStepReached(queryParams.financestep);
    }
  }, [queryParams.financestep, highestStepReached]);
  useEffect(function () {
    if (queryParams.ref === 'aribForm') {
      history.replaceState(null, "", window.location.pathname + window.location.search.replaceAll("&ref=aribForm", '').replaceAll("ref=aribForm", ''));
      setUserInfo(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null
        });
      });
    }
  }, [sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number]); // this will only trigger when userInfo changes on session

  useEffect(function () {
    if (!sessionUser) return;
    setUserInfo(function (prev) {
      if (prev.user_id != null && prev.user_id !== sessionUser.id) {
        if (accountSwitchInProgressRef.current) {
          accountSwitchInProgressRef.current = false;
          return _objectSpread(_objectSpread({}, prev), {}, {
            mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null,
            user_id: sessionUser.id
          });
        }
        return _objectSpread(_objectSpread({}, prev), {}, {
          first_name: null,
          fathers_name: null,
          family_name: null,
          mobile_number: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.phone_number) || null,
          city_id: (sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.city_id) || null,
          gender: null,
          id_number: null,
          birth_date: null,
          user_id: sessionUser.id
        });
      }
      if (prev.user_id == null) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          user_id: sessionUser.id
        });
      }
      return prev;
    });
  }, [sessionUser === null || sessionUser === void 0 ? void 0 : sessionUser.id]);

  // // Save userInfo
  useEffect(function () {
    setLocalStorage("arib_userInfo", JSON.stringify(userInfo));
  }, [userInfo]);

  // Save workInfo
  useEffect(function () {
    setLocalStorage("arib_workInfo", JSON.stringify(workInfo));
  }, [workInfo]);

  // Save orderData
  useEffect(function () {
    setLocalStorage("arib_orderData", JSON.stringify(orderData));
  }, [orderData]);
  useEffect(function () {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return;
    }

    // Nothing to read/write without a post id.
    if (!calculatorStorageId) {
      return;
    }
    var urlParams = new URLSearchParams(window.location.search);
    var encodedData = urlParams.get('finance_calculator_data');
    var handledFromUrl = false;
    if (encodedData) {
      var decodedData = decodeURIComponent(encodedData);
      try {
        var parsedData = JSON.parse(decodedData);
        setInstallmentCalculatorData(parsedData);
        // Persist to sessionStorage
        writeInstallmentCalculatorData(calculatorStorageId, parsedData);
        handledFromUrl = true;
      } catch (error) {
        console.error("Failed to parse finance_calculator_data:", error);
      }
    }

    // Re-read the snapshot whenever the post id changes. This provider is mounted
    // at the app root (client.tsx / renderer.tsx), NOT per route, so its first
    // render happens on whatever page the app booted on — usually /cardetail,
    // where there is no post_id query param yet. Relying on the lazy useState
    // initializer alone meant the snapshot was never picked up after navigating
    // to /finance-apply-arib?post_id=..., and the page fell back to the API's
    // finance_price defaults while sessionStorage held the user's real values.
    if (!handledFromUrl) {
      var storedData = readInstallmentCalculatorData(calculatorStorageId);
      if (storedData) {
        setInstallmentCalculatorData(storedData);
      }
    }

    // Listen for custom calculator update events.
    // Registered on every path — including the URL-param one — so later
    // calculator submits still reach the context.
    var handleCalculatorUpdate = function handleCalculatorUpdate(event) {
      if (event.detail && String(event.detail.postId) === String(calculatorStorageId)) {
        setInstallmentCalculatorData(event.detail.data);
      }
    };
    window.addEventListener(CALCULATOR_DATA_UPDATED_EVENT, handleCalculatorUpdate);
    return function () {
      window.removeEventListener(CALCULATOR_DATA_UPDATED_EVENT, handleCalculatorUpdate);
    };
  }, [calculatorStorageId]);
  var action__handleCalculatorDataChange = function action__handleCalculatorDataChange(calculatorData) {
    setInstallmentCalculatorData(calculatorData);
    // Persist to sessionStorage
    writeInstallmentCalculatorData(calculatorStorageId, calculatorData);
  };
  var action__clearLocalStorageData = function action__clearLocalStorageData() {
    removeLocalStorage("arib_userInfo");
    removeLocalStorage("arib_workInfo");
    removeLocalStorage("arib_orderData");
    removeLocalStorage(warrantyStorageKey);
  };
  var action__clearDataAfterSubmit = function action__clearDataAfterSubmit() {
    removeLocalStorage("arib_DataAfterSubmit");
  };
  var action__getPostID = function action__getPostID() {
    return (queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id) + "";
  };
  var action__backButtonClick = function action__backButtonClick() {
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 5) {
      action__clearLocalStorageData();
      action__clearDataAfterSubmit();
      return navigateTo("/cardetail/".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id));
    }
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 2) {
      navigateTo("/finance-apply-arib?post_id=".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id, "&financestep=1"));
      return;
    }
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 1) {
      navigateTo("/pre-checkout/".concat(queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id));
      return;
    }
    history.back();
  };
  var action__getCurrentTitle = function action__getCurrentTitle() {
    if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 1) {
      return t("personal_information_2", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 2) {
      return t("work_information", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 3) {
      return t("validation_required_documents", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 5) {
      return t("validation_required_documents", {
        ns: "web_checkout_finance"
      });
    } else if ((queryParams === null || queryParams === void 0 ? void 0 : queryParams.financestep) === 10) {
      return t("financing_eligibility", {
        ns: "web_checkout_finance"
      });
    }
    return ""; // Ensure it always returns a string
  };
  var action__getFinanceStep = function action__getFinanceStep() {
    return queryParams.financestep;
  };
  var action__updateUserInfo = function action__updateUserInfo(updates) {
    setUserInfo(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__beginAccountSwitch = function action__beginAccountSwitch() {
    accountSwitchInProgressRef.current = true;
  };
  var action__setWarrantyId = function action__setWarrantyId(id) {
    setWarrantyId(id);
    if (id != null) {
      setLocalStorage(warrantyStorageKey, String(id));
    } else {
      removeLocalStorage(warrantyStorageKey);
    }
  };
  var action__setRoadServiceId = function action__setRoadServiceId(id) {
    setRoadServiceId(id);
    if (id != null) {
      setLocalStorage(roadServiceStorageKey, String(id));
    } else {
      removeLocalStorage(roadServiceStorageKey);
    }
  };
  var action__updateWorkInfo = function action__updateWorkInfo(updates) {
    setWorkInfo(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__updateOrderInfo = function action__updateOrderInfo(updates) {
    setOrderData(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__updateDataAfterSubmit = function action__updateDataAfterSubmit(updates) {
    setDataAfterSubmit(function (prev) {
      return _objectSpread(_objectSpread({}, prev), updates);
    });
  };
  var action__validateUserInfo = function action__validateUserInfo() {
    var requiredFields = ["first_name", "fathers_name", "family_name", "mobile_number", "city_id", "gender", "id_number", "birth_date"];
    var fieldErrorMessages = {
      first_name: t("validation_first_name_required", {
        ns: "web_checkout_finance"
      }),
      fathers_name: t("validation_middle_name_required", {
        ns: "web_checkout_finance"
      }),
      family_name: t("validation_last_name_required", {
        ns: "web_checkout_finance"
      }),
      mobile_number: t("validation_phone_number_required", {
        ns: "web_checkout_finance"
      }),
      city_id: t("validation_city_required", {
        ns: "web_checkout_finance"
      }),
      gender: t("validation_gender_required", {
        ns: "web_checkout_finance"
      }),
      id_number: t("validation_id_number_required", {
        ns: "web_checkout_finance"
      }),
      birth_date: t("validation_date_birth_required", {
        ns: "web_checkout_finance"
      }),
      // If you require whatsapp_optin:
      whatsapp_optin: t("validation_whatsapp_option_required", {
        ns: "web_checkout_finance"
      })
    };
    var errors = {};
    for (var _i = 0, _requiredFields = requiredFields; _i < _requiredFields.length; _i++) {
      var field = _requiredFields[_i];
      if (!userInfo[field]) {
        errors[field] = fieldErrorMessages[field] || "Missing required field: ".concat(field);
      }
    }
    return errors; // empty object if no errors
  };
  var action__validateWorkInfo = function action__validateWorkInfo() {
    var requiredFields = ["work_sector", "salary", "salary_transfer_method", "other_financial_liabilities", "traffic_fine"];
    if (workInfo.other_financial_liabilities !== "0") {
      requiredFields.push("total_financial_liabilities");
    }
    if (workInfo.traffic_fine !== "0") {
      requiredFields.push("traffic_fine_value");
    }
    var fieldErrorMessages = {
      work_sector: t("select_workplace", {
        ns: "web_checkout_finance"
      }),
      salary: t("validation_salary_required", {
        ns: "web_checkout_finance"
      }),
      salary_transfer_method: t("select_salary_payment_method", {
        ns: "web_checkout_finance"
      }),
      employer_name: t("enter_workplace_name", {
        ns: "web_checkout_finance"
      }),
      termsAccepted: t("agree_2", {
        ns: "web_checkout_finance"
      }),
      other_financial_liabilities: t("enter_financial_obligations", {
        ns: "web_checkout_finance"
      }),
      traffic_fine: t("enter_traffic_violations", {
        ns: "web_checkout_finance"
      }),
      total_financial_liabilities: t("enter_total_financial_obligations", {
        ns: "web_checkout_finance"
      }),
      traffic_fine_value: t("enter_total_amount_traffic_fines", {
        ns: "web_checkout_finance"
      })
    };
    var errors = {};
    for (var _i2 = 0, _requiredFields2 = requiredFields; _i2 < _requiredFields2.length; _i2++) {
      var field = _requiredFields2[_i2];
      if (!workInfo[field]) {
        errors[field] = fieldErrorMessages[field] || "Missing required field: ".concat(field);
      }
    }
    return errors; // empty object if no errors
  };
  var fetch__FinancePostData = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(installmentPeriod) {
      var data, _ref5, post, warranty, _ref6, details, price, g4Data, _ref7, title, is_chinese, details_card, is_preowned, _ref8, finance_price, _ref9, post_condition, _ref0, have_finance, is_eligible_for_arib, post_ID, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            _context.p = 0;
            _context.n = 1;
            return api__getFinanceOrderDetails(_objectSpread(_objectSpread(_objectSpread({
              post_id: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id,
              include: FINANCE_POST_INCLUDE
            }, installmentPeriod ? {
              installment_period: installmentPeriod
            } : {}), warrantyId != null ? {
              warranty_id: warrantyId
            } : {}), roadServiceId != null ? {
              road_service_id: roadServiceId
            } : {}));
          case 1:
            data = _context.v;
            _ref5 = data || {}, post = _ref5.post, warranty = _objectWithoutProperties(_ref5, _excluded);
            _ref6 = post || {}, details = _ref6.details, price = _ref6.price, g4Data = _ref6.g4Data;
            _ref7 = details || {}, title = _ref7.h1, is_chinese = _ref7.is_chinese, details_card = _ref7.details_card, is_preowned = _ref7.is_preowned;
            _ref8 = price || {}, finance_price = _ref8.finance_price;
            _ref9 = g4Data || {}, post_condition = _ref9.post_condition;
            _ref0 = details || {}, have_finance = _ref0.have_finance, is_eligible_for_arib = _ref0.is_eligible_for_arib;
            post_ID = queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id;
            setPostData({
              title: title,
              finance_price: finance_price,
              post_condition: post_condition,
              g4Data: g4Data,
              have_finance: have_finance,
              is_eligible_for_arib: is_eligible_for_arib,
              post_id: post_ID,
              is_chinese: is_chinese,
              details_card: details_card,
              is_preowned: is_preowned
            });

            // warranty block ({finance_warrantyV2, finance_calculator_data}) returned
            // to the page so it can merge into its local checkoutOrder state.
            return _context.a(2, warranty);
          case 2:
            _context.p = 2;
            _t = _context.v;
            if (!(_t instanceof ReservedPostException)) {
              _context.n = 3;
              break;
            }
            return _context.a(2, navigateTo("/cardetail/".concat(_t.post_id, "?ref=car_reserved")));
          case 3:
            setFetching_error(_t);
          case 4:
            _context.p = 4;
            setFetch_loading(false);
            return _context.f(4);
          case 5:
            return _context.a(2);
        }
      }, _callee, null, [[0, 2, 4, 5]]);
    }));
    return function fetch__FinancePostData(_x) {
      return _ref4.apply(this, arguments);
    };
  }();
  var fetch__WorkSectorList = /*#__PURE__*/function () {
    var _ref1 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            return _context2.a(2, lang == 'ar' ? arib_work_list_ar : arib_work_list_en);
        }
      }, _callee2);
    }));
    return function fetch__WorkSectorList() {
      return _ref1.apply(this, arguments);
    };
  }();
  var fetch__BanksList = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee3() {
      return _regenerator().w(function (_context3) {
        while (1) switch (_context3.n) {
          case 0:
            return _context3.a(2, lang == 'ar' ? arib_bank_list_ar : arib_bank_list_en);
        }
      }, _callee3);
    }));
    return function fetch__BanksList() {
      return _ref10.apply(this, arguments);
    };
  }();
  var fetch__createAribJobForm = /*#__PURE__*/function () {
    var _ref11 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee4() {
      var params;
      return _regenerator().w(function (_context4) {
        while (1) switch (_context4.n) {
          case 0:
            setFetch_loading(true);
            params = {
              postId: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id,
              workInfo: workInfo,
              order_uuid: (orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid) || null
            };
            return _context4.a(2, api__createArib(params.postId, params.workInfo, params.order_uuid).then(function (res) {
              var _data$data, _data$data2, _data$data3, _data$data4;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              console.log(data, 'data from createAribJobForm');
              action__updateOrderInfo({
                order_uuid: (data === null || data === void 0 ? void 0 : (_data$data = data.data) === null || _data$data === void 0 ? void 0 : _data$data.order_uuid) || (orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid),
                order_id: (data === null || data === void 0 ? void 0 : (_data$data2 = data.data) === null || _data$data2 === void 0 ? void 0 : _data$data2.order_id) || (orderData === null || orderData === void 0 ? void 0 : orderData.order_id),
                payment_enabled: data === null || data === void 0 ? void 0 : (_data$data3 = data.data) === null || _data$data3 === void 0 ? void 0 : _data$data3.payment_enabled,
                amount: data === null || data === void 0 ? void 0 : (_data$data4 = data.data) === null || _data$data4 === void 0 ? void 0 : _data$data4.amount
              });
              return data;
            })["catch"](function (error) {
              console.log(error, 'error');
              setFetching_error(error);
              return error;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee4);
    }));
    return function fetch__createAribJobForm() {
      return _ref11.apply(this, arguments);
    };
  }();
  var fetch__createAribOrder = /*#__PURE__*/function () {
    var _ref12 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee5() {
      var financeData,
        userData,
        params,
        _args5 = arguments;
      return _regenerator().w(function (_context5) {
        while (1) switch (_context5.n) {
          case 0:
            financeData = _args5.length > 0 && _args5[0] !== undefined ? _args5[0] : {};
            setFetch_loading(true);
            userData = _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, userInfo), financeData || {}), warrantyId != null ? {
              warranty_id: warrantyId
            } : {}), roadServiceId != null ? {
              road_service_id: roadServiceId
            } : {});
            params = {
              userInfo: userData,
              postId: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id
            };
            return _context5.a(2, api__createAribOrder(params.userInfo, Number(params.postId)).then(function (res) {
              var _data$data5, _data$data6, _data$data7, _data$data8, _data$data9, _data$data0;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              var rangesArray = data !== null && data !== void 0 && (_data$data5 = data.data) !== null && _data$data5 !== void 0 && _data$data5.ranges ? Object.values(data === null || data === void 0 ? void 0 : (_data$data6 = data.data) === null || _data$data6 === void 0 ? void 0 : _data$data6.ranges) : [];
              action__updateOrderInfo({
                order_uuid: data === null || data === void 0 ? void 0 : (_data$data7 = data.data) === null || _data$data7 === void 0 ? void 0 : _data$data7.order_uuid,
                order_id: data === null || data === void 0 ? void 0 : (_data$data8 = data.data) === null || _data$data8 === void 0 ? void 0 : _data$data8.order_id,
                payment_enabled: data === null || data === void 0 ? void 0 : (_data$data9 = data.data) === null || _data$data9 === void 0 ? void 0 : _data$data9.payment_enabled,
                amount: data === null || data === void 0 ? void 0 : (_data$data0 = data.data) === null || _data$data0 === void 0 ? void 0 : _data$data0.amount
              });
              return data;
            })["catch"](function (error) {
              setFetching_error(error);
              console.log(error);
              return null;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee5);
    }));
    return function fetch__createAribOrder() {
      return _ref12.apply(this, arguments);
    };
  }();
  var fetch__AribSendOtop = /*#__PURE__*/function () {
    var _ref13 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee6() {
      var params;
      return _regenerator().w(function (_context6) {
        while (1) switch (_context6.n) {
          case 0:
            setFetch_loading(true);
            params = {
              order_uuid: orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid
            };
            return _context6.a(2, api__aribSendOtp(params.order_uuid).then(function (res) {
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              return data;
            })["catch"](function (error) {
              console.log(error, 'error');
              setFetching_error(error);
              return error;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee6);
    }));
    return function fetch__AribSendOtop() {
      return _ref13.apply(this, arguments);
    };
  }();
  var fetch__VerifyAribOtp = /*#__PURE__*/function () {
    var _ref14 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee7(code) {
      var params, fireAribResponseTrackEvent;
      return _regenerator().w(function (_context7) {
        while (1) switch (_context7.n) {
          case 0:
            setFetch_loading(true);
            params = {
              transaction_id: orderData === null || orderData === void 0 ? void 0 : orderData.transaction_id,
              order_uuid: orderData === null || orderData === void 0 ? void 0 : orderData.order_uuid,
              otp: code
            };
            fireAribResponseTrackEvent = function fireAribResponseTrackEvent(arib_eligibility_status, arib_response_status) {
              var _postData$g4Data, _postData$g4Data2, _postData$g4Data3, _postData$g4Data4;
              trackEvents("arib_response", {
                post_id: queryParams === null || queryParams === void 0 ? void 0 : queryParams.post_id,
                post_make: postData === null || postData === void 0 ? void 0 : (_postData$g4Data = postData.g4Data) === null || _postData$g4Data === void 0 ? void 0 : _postData$g4Data.post_make,
                post_model: postData === null || postData === void 0 ? void 0 : (_postData$g4Data2 = postData.g4Data) === null || _postData$g4Data2 === void 0 ? void 0 : _postData$g4Data2.post_model,
                post_year: postData === null || postData === void 0 ? void 0 : (_postData$g4Data3 = postData.g4Data) === null || _postData$g4Data3 === void 0 ? void 0 : _postData$g4Data3.post_year,
                inventory_type: postData === null || postData === void 0 ? void 0 : (_postData$g4Data4 = postData.g4Data) === null || _postData$g4Data4 === void 0 ? void 0 : _postData$g4Data4.inventory_type_string,
                post_condition: postData === null || postData === void 0 ? void 0 : postData.post_condition,
                order_id: orderData === null || orderData === void 0 ? void 0 : orderData.order_id,
                user_salary: workInfo === null || workInfo === void 0 ? void 0 : workInfo.salary,
                arib_eligibility_status: arib_eligibility_status,
                arib_response_status: arib_response_status
              });
            };
            return _context7.a(2, api__verifyAribOtp(params.transaction_id, params.order_uuid, params.otp).then(function (res) {
              var _data$data1, _data$data10;
              var data = (res === null || res === void 0 ? void 0 : res.data) || null;
              if (data === null) {
                fireAribResponseTrackEvent("could not check", "fail");
              } else if ((data === null || data === void 0 ? void 0 : (_data$data1 = data.data) === null || _data$data1 === void 0 ? void 0 : _data$data1.code) != "000" || !(data !== null && data !== void 0 && (_data$data10 = data.data) !== null && _data$data10 !== void 0 && _data$data10.isEligible)) {
                fireAribResponseTrackEvent("not eligible", "success");
              } else {
                fireAribResponseTrackEvent("eligible", "success");
              }
              return data;
            })["catch"](function (error) {
              setFetching_error(error);
              return null;
            })["finally"](function () {
              setFetch_loading(false);
            }));
        }
      }, _callee7);
    }));
    return function fetch__VerifyAribOtp(_x2) {
      return _ref14.apply(this, arguments);
    };
  }();
  var fetch__deleteFinanceOrder = /*#__PURE__*/function () {
    var _ref15 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee8(order_uuid) {
      return _regenerator().w(function (_context8) {
        while (1) switch (_context8.n) {
          case 0:
            return _context8.a(2, api__deleteFinanceOrder({
              order_uuid: order_uuid
            }));
        }
      }, _callee8);
    }));
    return function fetch__deleteFinanceOrder(_x3) {
      return _ref15.apply(this, arguments);
    };
  }();

  // const helper__paymentEnabled = () => useCallback(() => {
  //         return orderData?.payment_enabled && orderData?.amount > 0;
  // }, [orderData?.amount, orderData?.payment_enabled]);

  var helper__paymentEnabled = useCallback(function () {
    return (orderData === null || orderData === void 0 ? void 0 : orderData.payment_enabled) && (orderData === null || orderData === void 0 ? void 0 : orderData.amount) > 0;
  }, [orderData === null || orderData === void 0 ? void 0 : orderData.amount, orderData === null || orderData === void 0 ? void 0 : orderData.payment_enabled]);
  var helper__getSalaryRangeFee = function helper__getSalaryRangeFee() {
    var _workInfo$salary, _orderData$ranges;
    var salary = workInfo === null || workInfo === void 0 ? void 0 : (_workInfo$salary = workInfo.salary) === null || _workInfo$salary === void 0 ? void 0 : _workInfo$salary.split(",").join("");
    var salaryRange = orderData === null || orderData === void 0 ? void 0 : (_orderData$ranges = orderData.ranges) === null || _orderData$ranges === void 0 ? void 0 : _orderData$ranges.find(function (range) {
      if (!range.min_salary || !range.max_salary) {
        return false;
      }
      if (salary < (range === null || range === void 0 ? void 0 : range.min_salary) || salary > (range === null || range === void 0 ? void 0 : range.max_salary)) {
        return false;
      }
      if (salary >= (range === null || range === void 0 ? void 0 : range.min_salary) && salary <= (range === null || range === void 0 ? void 0 : range.max_salary)) {
        return true;
      }
    });
    if ((salaryRange === null || salaryRange === void 0 ? void 0 : salaryRange.payment_amount) == 0) {
      return 0;
    }
    return parseInt((salaryRange === null || salaryRange === void 0 ? void 0 : salaryRange.payment_amount) || (orderData === null || orderData === void 0 ? void 0 : orderData.payment_fee_default_value));
  };
  var helper__getAribPaymentType = function helper__getAribPaymentType() {
    if (helpers.helper__paymentEnabled()) {
      return "paid";
    }
    return "free";
  };
  var helper__getFinanceCalculatorData = function helper__getFinanceCalculatorData() {
    var _installmentCalculato, _postData$finance_pri, _installmentCalculato2, _postData$finance_pri2, _installmentCalculato3, _postData$finance_pri3, _installmentCalculato4, _postData$finance_pri4;
    var installmentPeriod = (_installmentCalculato = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.period) !== null && _installmentCalculato !== void 0 ? _installmentCalculato : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri = postData.finance_price) === null || _postData$finance_pri === void 0 ? void 0 : _postData$finance_pri.installment_period;
    var down_payment = (_installmentCalculato2 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.down_payment) !== null && _installmentCalculato2 !== void 0 ? _installmentCalculato2 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri2 = postData.finance_price) === null || _postData$finance_pri2 === void 0 ? void 0 : _postData$finance_pri2.first_payment;
    var last_payment = (_installmentCalculato3 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.last_payment) !== null && _installmentCalculato3 !== void 0 ? _installmentCalculato3 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri3 = postData.finance_price) === null || _postData$finance_pri3 === void 0 ? void 0 : _postData$finance_pri3.last_payment;
    var monthly_installment = (_installmentCalculato4 = installmentCalculatorData === null || installmentCalculatorData === void 0 ? void 0 : installmentCalculatorData.monthly_installment) !== null && _installmentCalculato4 !== void 0 ? _installmentCalculato4 : postData === null || postData === void 0 ? void 0 : (_postData$finance_pri4 = postData.finance_price) === null || _postData$finance_pri4 === void 0 ? void 0 : _postData$finance_pri4.text;
    return {
      installmentPeriod: installmentPeriod || 0,
      down_payment: down_payment || 0,
      last_payment: last_payment || 0,
      monthly_installment: monthly_installment || 0
    };
  };

  /** Collections **/
  var actions = {
    action__getPostID: action__getPostID,
    action__backButtonClick: action__backButtonClick,
    action__getCurrentTitle: action__getCurrentTitle,
    action__getFinanceStep: action__getFinanceStep,
    action__updateUserInfo: action__updateUserInfo,
    action__beginAccountSwitch: action__beginAccountSwitch,
    action__setWarrantyId: action__setWarrantyId,
    action__setRoadServiceId: action__setRoadServiceId,
    action__updateWorkInfo: action__updateWorkInfo,
    action__updateOrderInfo: action__updateOrderInfo,
    action__validateUserInfo: action__validateUserInfo,
    action__validateWorkInfo: action__validateWorkInfo,
    action__clearLocalStorageData: action__clearLocalStorageData,
    action__clearDataAfterSubmit: action__clearDataAfterSubmit,
    action__updateDataAfterSubmit: action__updateDataAfterSubmit,
    action__handleCalculatorDataChange: action__handleCalculatorDataChange
  };
  var remote_actions = {
    fetch__FinancePostData: fetch__FinancePostData,
    fetch__WorkSectorList: fetch__WorkSectorList,
    fetch__BanksList: fetch__BanksList,
    fetch__createAribJobForm: fetch__createAribJobForm,
    fetch__AribSendOtop: fetch__AribSendOtop,
    fetch__VerifyAribOtp: fetch__VerifyAribOtp,
    fetch__deleteFinanceOrder: fetch__deleteFinanceOrder,
    fetch__createAribOrder: fetch__createAribOrder
  };
  var helpers = {
    helper__paymentEnabled: helper__paymentEnabled,
    helper__getAribPaymentType: helper__getAribPaymentType,
    helper__getFinanceCalculatorData: helper__getFinanceCalculatorData
  };
  var state = {
    userInfo: userInfo,
    warrantyId: warrantyId,
    roadServiceId: roadServiceId,
    workInfo: workInfo,
    fetching_error: fetching_error,
    fetch_loading: fetch_loading,
    postData: postData,
    orderData: orderData,
    dataAfterSubmit: dataAfterSubmit,
    installmentCalculatorData: installmentCalculatorData
  };
  return /*#__PURE__*/React.createElement(AribFinanceContext.Provider, {
    value: {
      actions: actions,
      remote_actions: remote_actions,
      state: state,
      queryParams: queryParams,
      helpers: helpers
    }
  }, children);
};